/**
 * Eye calibration: run after Start (EYES mode only) and re-runnable mid-play via
 * hotkey C (see `./game`). Walks the player through five dot positions — centre,
 * up, down, left, right, each `CALIBRATION_PHASE_MS` long with the first
 * `CALIBRATION_DISCARD_MS` of each phase discarded so the player's eyes have time
 * to settle onto the new dot — bucketing the RAW `toEyeSample().gaze` values per
 * phase, then resolving with `calibrationFromSamples`'s result (or
 * `DEFAULT_CALIBRATION` on Skip/Escape).
 *
 * `calibrationStepAt` is the pure phase/sample-bucketing helper (unit-tested in
 * `test/client/calibration.test.ts`); `CalibrationOverlay` is the DOM/time-driven
 * wiring around it and is intentionally untested (same convention as
 * `client/game.ts`'s RAF loop and `client/scare/scene.ts`'s overlay) — it has no
 * RAF/timer loop of its own, it only reacts to `feedSample` calls the caller makes
 * once per frame, so all the actual timing math lives in the pure helper above it.
 */

import type { GazeCalibration, Vec2 } from "../src";
import { calibrationFromSamples, DEFAULT_CALIBRATION } from "../src";

export type CalibrationPhase = "neutral" | "up" | "down" | "left" | "right";

/** Order the overlay walks the player through — "neutral" first (the centre dot),
 * matching `calibrationFromSamples`'s `neutral` sample-bucket name directly so no
 * phase-name mapping is needed between the UI and the core contract. */
export const CALIBRATION_PHASES: readonly CalibrationPhase[] = [
  "neutral",
  "up",
  "down",
  "left",
  "right",
];

export const CALIBRATION_PHASE_MS = 1000;
export const CALIBRATION_DISCARD_MS = 300;

export interface CalibrationStep {
  readonly phase: CalibrationPhase;
  readonly phaseIndex: number;
  /** ms elapsed within the current phase (clamped to `CALIBRATION_PHASE_MS` once `done`). */
  readonly phaseElapsedMs: number;
  /** True once `phaseElapsedMs` has passed the discard window — a sample taken now should be kept. */
  readonly shouldSample: boolean;
  /** True once every phase has run its full duration — the caller should finalize. */
  readonly done: boolean;
}

/**
 * Pure: maps elapsed ms since calibration started to which phase is active right
 * now, whether this instant is past that phase's discard window, and whether the
 * whole sequence is done. Total duration is `CALIBRATION_PHASES.length *
 * CALIBRATION_PHASE_MS`. `elapsedMs` is clamped to `>= 0` so a caller passing a
 * `nowMs` that (through clock jitter) lands a hair before the recorded start
 * doesn't wrap to the last phase.
 */
export function calibrationStepAt(elapsedMs: number): CalibrationStep {
  const totalMs = CALIBRATION_PHASES.length * CALIBRATION_PHASE_MS;
  const clamped = Math.max(0, elapsedMs);

  if (clamped >= totalMs) {
    const lastIndex = CALIBRATION_PHASES.length - 1;
    return {
      phase: CALIBRATION_PHASES[lastIndex]!,
      phaseIndex: lastIndex,
      phaseElapsedMs: CALIBRATION_PHASE_MS,
      shouldSample: false,
      done: true,
    };
  }

  const phaseIndex = Math.min(
    CALIBRATION_PHASES.length - 1,
    Math.floor(clamped / CALIBRATION_PHASE_MS),
  );
  const phaseElapsedMs = clamped - phaseIndex * CALIBRATION_PHASE_MS;
  return {
    phase: CALIBRATION_PHASES[phaseIndex]!,
    phaseIndex,
    phaseElapsedMs,
    shouldSample: phaseElapsedMs >= CALIBRATION_DISCARD_MS,
    done: false,
  };
}

const INSTRUCTION_TEXT = "Keep your head still — follow the dot with your eyes only.";

const DOT_POSITION: Record<CalibrationPhase, { left: string; top: string }> = {
  neutral: { left: "50%", top: "50%" },
  up: { left: "50%", top: "12%" },
  down: { left: "50%", top: "88%" },
  left: { left: "12%", top: "50%" },
  right: { left: "88%", top: "50%" },
};

type SampleBuckets = Record<CalibrationPhase, Vec2[]>;

function emptyBuckets(): SampleBuckets {
  return { neutral: [], up: [], down: [], left: [], right: [] };
}

export class CalibrationOverlay {
  readonly #root: HTMLElement;
  readonly #overlay: HTMLDivElement;
  readonly #dot: HTMLDivElement;

  #active = false;
  #startMs = 0;
  #lastPhase: CalibrationPhase | null = null;
  #buckets: SampleBuckets = emptyBuckets();
  #resolve: ((cal: GazeCalibration) => void) | null = null;

  readonly #onKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.#cancel();
  };

  constructor(root: HTMLElement) {
    this.#root = root;

    this.#overlay = document.createElement("div");
    this.#overlay.setAttribute("role", "dialog");
    this.#overlay.setAttribute("aria-modal", "true");
    this.#overlay.setAttribute("aria-label", "Eye calibration");
    this.#overlay.style.position = "fixed";
    this.#overlay.style.inset = "0";
    this.#overlay.style.zIndex = "1800";
    this.#overlay.style.background = "rgba(8, 8, 12, 0.92)";
    this.#overlay.style.display = "none";

    const liveText = document.createElement("div");
    liveText.setAttribute("role", "status");
    liveText.setAttribute("aria-live", "polite");
    liveText.textContent = INSTRUCTION_TEXT;
    liveText.style.position = "absolute";
    liveText.style.top = "16px";
    liveText.style.left = "0";
    liveText.style.right = "0";
    liveText.style.textAlign = "center";
    liveText.style.color = "#d8d8e8";
    liveText.style.font = "14px system-ui, sans-serif";

    this.#dot = document.createElement("div");
    this.#dot.style.position = "absolute";
    this.#dot.style.width = "16px";
    this.#dot.style.height = "16px";
    this.#dot.style.marginLeft = "-8px";
    this.#dot.style.marginTop = "-8px";
    this.#dot.style.borderRadius = "50%";
    this.#dot.style.background = "#ff5d73";
    this.#dot.style.transition = "left 120ms ease-out, top 120ms ease-out";

    const skip = document.createElement("button");
    skip.type = "button";
    skip.textContent = "Skip";
    skip.style.position = "absolute";
    skip.style.bottom = "16px";
    skip.style.right = "16px";
    skip.addEventListener("click", () => this.#cancel());

    this.#overlay.appendChild(liveText);
    this.#overlay.appendChild(this.#dot);
    this.#overlay.appendChild(skip);
    this.#root.appendChild(this.#overlay);
  }

  get active(): boolean {
    return this.#active;
  }

  /** Mounts the overlay and starts phase timing at `nowMs`. Resolves once every
   * phase completes (with `calibrationFromSamples`'s result) or the player hits
   * Skip/Escape (with `DEFAULT_CALIBRATION`). */
  run(nowMs: number): Promise<GazeCalibration> {
    this.#active = true;
    this.#startMs = nowMs;
    this.#lastPhase = null;
    this.#buckets = emptyBuckets();
    this.#overlay.style.display = "block";
    this.#setPhase("neutral");
    document.addEventListener("keydown", this.#onKeydown);
    return new Promise<GazeCalibration>((resolve) => {
      this.#resolve = resolve;
    });
  }

  /** Call once per RAF frame while `active` is true, with the frame's raw
   * `toEyeSample().gaze` (or null when no face is currently detected — that frame
   * still advances phase timing, it just contributes no sample). No-ops when not
   * active, so it's safe to call unconditionally from the caller's frame loop. */
  feedSample(nowMs: number, rawGaze: Vec2 | null): void {
    if (!this.#active) return;
    const step = calibrationStepAt(nowMs - this.#startMs);
    if (step.phase !== this.#lastPhase) this.#setPhase(step.phase);
    if (step.shouldSample && rawGaze !== null) {
      this.#buckets[step.phase].push(rawGaze);
    }
    if (step.done) this.#finish(calibrationFromSamples(this.#buckets));
  }

  #setPhase(phase: CalibrationPhase): void {
    this.#lastPhase = phase;
    const pos = DOT_POSITION[phase];
    this.#dot.style.left = pos.left;
    this.#dot.style.top = pos.top;
  }

  #finish(cal: GazeCalibration): void {
    if (!this.#active) return;
    this.#active = false;
    this.#teardown();
    console.log("scarymaze: calibration", cal);
    const resolve = this.#resolve;
    this.#resolve = null;
    resolve?.(cal);
  }

  #cancel(): void {
    if (!this.#active) return;
    this.#active = false;
    this.#teardown();
    const resolve = this.#resolve;
    this.#resolve = null;
    resolve?.(DEFAULT_CALIBRATION);
  }

  #teardown(): void {
    this.#overlay.style.display = "none";
    document.removeEventListener("keydown", this.#onKeydown);
  }

  /** Idempotent hard-stop for the outer game loop's teardown (`GameLoopHandle.stop`)
   * — removes the DOM overlay/listener without resolving `run()`'s promise (the
   * loop is going away, there's nothing left to resume into). Safe to call even
   * when never active. */
  dispose(): void {
    document.removeEventListener("keydown", this.#onKeydown);
    this.#overlay.remove();
  }
}
